/**
 * Relations compound tool — create, batch-create, dismiss, delete.
 * Consolidates relate-entries, batch-relate, dismiss-suggestion into one write tool.
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { kernelQuery, kernelMutation, getWorkspaceContext, getAgentSessionId, requireWriteAccess, runWithToolContext } from "../client.js";
import { success, thinWrapper, parseOrFail, notFoundResult, successResult, failureResult, type NextAction, type ToolResult } from "../envelope.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";
import { handleFind, handleSuggest } from "./graph.js";

// WP-484 S2 (§3): `relations` absorbs `graph` → find/suggest. Legacy `graph`
// tool stays registered alongside (§7 "alongside" mechanic) — both call the
// SAME handleFind/handleSuggest functions, one business-logic path.
const RELATIONS_ACTIONS = ["create", "batch-create", "dismiss", "delete", "find", "suggest"] as const;
type RelationsAction = (typeof RELATIONS_ACTIONS)[number];

const relationItemSchema = z.object({
  from: z.string().max(200),
  to: z.string().max(200),
  type: z.string().max(200),
});

// §3a — the REGISTERED layer: flat ZodObject, action enum + superset of params.
export const relationsSchema = z.object({
  action: z.enum(RELATIONS_ACTIONS).describe(
    "'create': link two entries. 'batch-create': create multiple relations (validates every item before writing any). " +
      "'dismiss': record that a suggestion was not relevant. 'delete': remove a relation. " +
      "'find': traverse relations from an entry (graph walk, absorbs graph action=find). " +
      "'suggest': discover potential connections for an entry (absorbs graph action=suggest).",
  ),
  from: z.string().max(200).optional().describe("For 'create'/'dismiss'/'delete': source entry ID."),
  to: z.string().max(200).optional().describe("For 'create'/'dismiss'/'delete': target entry ID."),
  type: z.string().max(200).optional().describe("For 'create'/'dismiss'/'delete': relation type."),
  score: z.number().optional().describe("For 'dismiss': suggestion score from action=suggest."),
  relations: z.array(relationItemSchema).min(1).max(20).optional().describe("For 'batch-create': array of {from, to, type}."),
  // WP-316 S3: Preview gate — dry-run mode for action=create.
  preview: z.boolean().optional().describe("For 'create': if true, validates the relation without writing. Returns what would happen. Default false."),
  entryId: z.string().max(200).optional().describe("For 'find'/'suggest': entry ID, e.g. '<PREFIX>-<n>'."),
  direction: z.enum(["incoming", "outgoing", "both"]).optional().describe("For 'find': 'incoming' = what references this, 'outgoing' = what this references. Default 'both'."),
  limit: z.number().min(1).max(20).optional().describe("For 'suggest': max suggestions to return. Default 10."),
  depth: z.number().min(1).max(3).optional().describe("For 'suggest': graph traversal depth. Default 2."),
});

// §3b — the HANDLER layer: per-action discriminatedUnion.
const relationsCreateVariant = z.object({
  action: z.literal("create"),
  from: z.string().max(200),
  to: z.string().max(200),
  type: z.string().max(200),
  score: z.number().optional(),
  preview: z.boolean().optional(),
});
const relationsBatchCreateVariant = z.object({
  action: z.literal("batch-create"),
  relations: z.array(relationItemSchema).min(1).max(20),
});
const relationsDismissVariant = z.object({
  action: z.literal("dismiss"),
  from: z.string().max(200),
  to: z.string().max(200),
  type: z.string().max(200).optional(),
  score: z.number().optional(),
});
const relationsDeleteVariant = z.object({
  action: z.literal("delete"),
  from: z.string().max(200),
  to: z.string().max(200),
  type: z.string().max(200),
});
const relationsFindVariant = z.object({
  action: z.literal("find"),
  entryId: z.string().max(200),
  direction: z.enum(["incoming", "outgoing", "both"]).optional().default("both"),
});
const relationsSuggestVariant = z.object({
  action: z.literal("suggest"),
  entryId: z.string().max(200),
  limit: z.number().min(1).max(20).optional().default(10),
  depth: z.number().min(1).max(3).optional().default(2),
});

export const relationsActionUnion = z.discriminatedUnion("action", [
  relationsCreateVariant,
  relationsBatchCreateVariant,
  relationsDismissVariant,
  relationsDeleteVariant,
  relationsFindVariant,
  relationsSuggestVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const RELATIONS_ACTION_SPECS: Record<RelationsAction, ActionParamSpec> = {
  create: { params: ["from", "to", "type", "score", "preview"], description: "from, to, and type are required." },
  "batch-create": { params: ["relations"], description: "relations (1-20 items) is required." },
  dismiss: { params: ["from", "to", "type", "score"], description: "from and to are required." },
  delete: { params: ["from", "to", "type"], description: "from, to, and type are required." },
  find: { params: ["entryId", "direction"], description: "entryId is required." },
  suggest: { params: ["entryId", "limit", "depth"], description: "entryId is required." },
};

export function registerRelationsTools(server: McpServer): void {
  // §3b handler map — one entry per action, dispatched via dispatchDiscriminated.
  const relationsHandlers: Record<RelationsAction, (data: any) => Promise<ToolResult>> = {
    create: (data) => handleCreate(data.from, data.to, data.type, data.score, data.preview),
    "batch-create": (data) => handleBatchCreate(data.relations),
    dismiss: (data) => handleDismiss(data.from, data.to, data.type ?? "related_to", data.score),
    delete: (data) => handleDelete(data.from, data.to, data.type),
    find: (data) => handleFind(data.entryId, data.direction ?? "both"),
    suggest: (data) => handleSuggest(data.entryId, data.limit ?? 10, data.depth ?? 2),
  };

  server.registerTool(
    "relations",
    {
      title: "Relations",
      description:
        "Create, manage, and discover relations between entries.\n\n" +
        "- **create**: Link two entries with a typed relation. Use action=suggest to discover connections.\n" +
        "- **batch-create**: Create multiple relations in one call (validates every item before writing any). Use after action=suggest.\n" +
        "- **dismiss**: Record that a suggested link was not relevant. Improves future suggestion quality.\n" +
        "- **delete**: Remove a relation by from, to, and type.\n" +
        "- **find**: Traverse relations from a specific entry — shows incoming/outgoing connections. NOT full-text search (use entries action=search).\n" +
        "- **suggest**: Discover potential relations for an entry using graph-aware intelligence. Returns ranked suggestions with confidence scores.",
      inputSchema: relationsSchema,
      // Mixed read/write noun (§3 R3): find/suggest are read-only, create/batch-create/dismiss/delete write.
      annotations: { readOnlyHint: false, destructiveHint: true, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(relationsSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "relations", action }, () =>
        dispatchDiscriminated("relations", relationsActionUnion, parsed.data, RELATIONS_ACTION_SPECS, relationsHandlers),
      );
    }),
  );
}

async function handleCreate(from: string, to: string, type: string, score?: number, preview?: boolean) {
  requireWriteAccess();
  const agentSessionId = getAgentSessionId();

  type CreateResult = {
    preview?: boolean;
    status?: string;
    chainDebtPrevented?: boolean;
    proposalId?: string;
    originalType?: string;
    suggestedAlternatives?: Array<{ type: string; reason: string }>;
    existing?: boolean;
    fromEntryId?: string;
    toEntryId?: string;
    message?: string;
    relationId?: string;
    suggestedType?: { type: string; confidence: number; reasoning: string; governancePosture?: string };
  };
  const result = await kernelMutation<GatewayRouteReturnByName<"chain.createEntryRelation">>("chain.createEntryRelation", {
    fromEntryId: from,
    toEntryId: to,
    type,
    suggestionScore: score ?? undefined,
    // WP-474 E4-REWIRE: explicit relate = human/agent-CHOSEN type → mark "user"
    // (DEC-1399: out of gate scope, but positively marked so the judge never keys off absence).
    proposedBy: "user",
    sessionId: agentSessionId ?? undefined,
    ...(preview ? { preview: true } : {}),
  });

  // WP-363 S5: Chain debt prevented — misuse pattern intercepted, agent-proposal created.
  if (result?.chainDebtPrevented) {
    const alts = result.suggestedAlternatives ?? [];
    const altList = alts.map((a) => `  • \`${a.type}\`: ${a.reason}`).join("\n");
    return {
      content: [{
        type: "text" as const,
        text: `# Chain Debt Prevented\n\n**"${result.originalType ?? type}"** was intercepted as a common misuse for this entry pair and converted to an agent sign-off proposal.\n\n**Proposal ID:** \`${result.proposalId}\`\n\n**Suggested alternatives:**\n${altList}\n\nReview at /review in Cortex UI.`,
      }],
      structuredContent: success(
        `Chain debt prevented: "${result.originalType ?? type}" was intercepted and converted to an agent sign-off proposal.`,
        {
          chainDebtPrevented: true,
          proposalId: result.proposalId,
          originalType: result.originalType ?? type,
          suggestedAlternatives: result.suggestedAlternatives,
        },
        [
          { tool: "relations", description: "Use correct type (e.g. informed_by)", parameters: { action: "create", from, to, type: alts[0]?.type ?? "informed_by" } },
        ],
      ),
    };
  }

  // WP-316 S3: Preview gate — return dry-run result without recording session activity.
  if (result?.preview) {
    const suggested = result.suggestedType;
    // WP-316 gap fix: add governance posture so agents know whether the suggested type requires consent.
    const suggestedWithPosture = suggested
      ? { ...suggested, governancePosture: suggested.type === "governs" ? "requires_consent" : "direct" }
      : undefined;
    return {
      content: [{ type: "text" as const, text: `# Preview: would relate ${from} → ${to} (${type})\n\nNo DB writes — call without \`preview:true\` to create for real.${suggestedWithPosture ? `\n\n**Type suggestion:** \`${suggestedWithPosture.type}\` may be more precise (${suggestedWithPosture.confidence}% confidence). Governance: ${suggestedWithPosture.governancePosture}.` : ""}` }],
      structuredContent: success(
        `Preview: would relate ${from} → ${to} (${type}) — no DB writes`,
        {
          status: "preview" as const, from, to, type,
          ...(suggestedWithPosture && { suggestedType: suggestedWithPosture }),
        },
        [{ tool: "relations", description: "Create for real", parameters: { action: "create", from, to, type } }],
      ),
    };
  }

  const wsCtx = await getWorkspaceContext();
  if (result?.status === "proposal_created") {
    const existingNote = result.existing ? " (existing proposal reused)" : "";
    return {
      content: [{
        type: "text" as const,
        text: `# Proposal Created — Governs Relation\n\n**${result.fromEntryId ?? from}** —[${type}]→ **${result.toEntryId ?? to}**\n\n${result.message ?? "Governs relation requires async consent. A proposal was created for review."}\n\n**Proposal ID:** \`${result.proposalId}\`${existingNote}\n**Workspace:** ${wsCtx.workspaceSlug} (${wsCtx.workspaceId})\n\nThe relation was **not applied** — it will be created when the proposal is approved.`,
      }],
      structuredContent: success(
        `Proposal created for ${from} → ${to} (${type}). Awaiting consent.`,
        { status: "proposal_created" as const, from, to, type, proposalId: result.proposalId },
      ),
    };
  }

  const lines = [
    `# Relation Created`,
    "",
    `**${from}** —[${type}]→ **${to}**`,
    `**Workspace:** ${wsCtx.workspaceSlug} (${wsCtx.workspaceId})`,
  ];

  const suggested = result?.suggestedType;
  // WP-316 gap fix: add governance posture so agents know whether the suggested type requires consent.
  const suggestedWithPosture = suggested
    ? { ...suggested, governancePosture: suggested.type === "governs" ? "requires_consent" : "direct" }
    : undefined;
  if (suggestedWithPosture) {
    lines.push("");
    lines.push(`**Type suggestion:** \`${suggestedWithPosture.type}\` may be more precise (${suggestedWithPosture.confidence}% confidence). Governance: ${suggestedWithPosture.governancePosture}.`);
    lines.push(`_${suggestedWithPosture.reasoning}_`);
    lines.push(`To use instead: \`relations action=create from="${from}" to="${to}" type="${suggestedWithPosture.type}"\``);
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Created relation ${from} → ${to} (${type}).`,
      {
        status: "created" as const, from, to, type,
        ...(suggestedWithPosture && { suggestedType: suggestedWithPosture }),
      },
      [{ tool: "relations", description: "See connections", parameters: { action: "find", entryId: from } }],
    ),
  };
}

async function handleBatchCreate(relations: Array<{ from: string; to: string; type: string }>) {
  requireWriteAccess();
  const agentSessionId = getAgentSessionId();

  type RelResult = { from: string; to: string; type: string; ok: boolean; error?: string; proposalCreated?: boolean; proposalId?: string };
  const results: RelResult[] = [];

  // Governs relations need proposal flow — use single mutation
  const governsRels = relations.filter(r => r.type === "governs");
  const batchRels = relations.filter(r => r.type !== "governs");

  // Batch non-governs in one transaction (BET-105 E4)
  if (batchRels.length > 0) {
    try {
      const batchResult = await kernelMutation<GatewayRouteReturnByName<"chain.createEntryRelations">>("chain.createEntryRelations", {
        // WP-474 E4-REWIRE: explicit relate tool creates CHOSEN edges — mark provenance
        // "user" (no confidence; human-chosen) so the census can distinguish them.
        relations: batchRels.map(r => ({ fromEntryId: r.from, toEntryId: r.to, type: r.type, proposedBy: "user" })),
        sessionId: agentSessionId ?? undefined,
      });
      for (let i = 0; i < batchRels.length; i++) {
        const rel = batchRels[i];
        const res = batchResult.results[i];
        results.push({ ...rel, ok: res?.ok ?? false, error: res?.error });
      }
    } catch (e: unknown) {
      for (const rel of batchRels) {
        results.push({ ...rel, ok: false, error: e instanceof Error ? e.message : "Batch failed" });
      }
    }
  }

  // Governs relations individually (proposal flow requires single mutation)
  for (const rel of governsRels) {
    try {
      const result = await kernelMutation<GatewayRouteReturnByName<"chain.createEntryRelation">>("chain.createEntryRelation", {
        fromEntryId: rel.from,
        toEntryId: rel.to,
        type: rel.type,
        // WP-474 E4-REWIRE: explicit batch-relate governs = human/agent-CHOSEN → "user".
        proposedBy: "user",
        sessionId: agentSessionId ?? undefined,
      });
      results.push({
        ...rel,
        ok: true,
        proposalCreated: result?.status === "proposal_created",
        proposalId: result?.proposalId,
      });
    } catch (e: unknown) {
      results.push({ ...rel, ok: false, error: e instanceof Error ? e.message : "Unknown error" });
    }
  }

  const created = results.filter((r) => r.ok && !r.proposalCreated);
  const proposals = results.filter((r) => r.ok && r.proposalCreated);
  const failed = results.filter((r) => !r.ok);

  const lines = [`# Batch Link Results\n`];
  lines.push(`**${created.length}** created, **${proposals.length}** proposals, **${failed.length}** failed out of ${relations.length} total.\n`);

  if (created.length > 0) {
    lines.push("## Created");
    for (const r of created) {
      lines.push(`- **${r.from}** —[${r.type}]→ **${r.to}**`);
    }
  }

  if (proposals.length > 0) {
    lines.push("");
    lines.push("## Proposals (governs — not yet applied)");
    for (const r of proposals) {
      lines.push(`- **${r.from}** —[${r.type}]→ **${r.to}** — proposal \`${r.proposalId}\``);
    }
  }

  if (failed.length > 0) {
    lines.push("");
    lines.push("## Failed");
    for (const r of failed) {
      lines.push(`- **${r.from}** → **${r.to}** (${r.type}): _${r.error}_`);
    }
  }

  const next: NextAction[] = failed.length === 0
    ? [{ tool: "relations", description: "Check connections", parameters: { action: "find", entryId: relations[0].from } }]
    : [];

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Batch: ${created.length} created, ${proposals.length} proposals, ${failed.length} failed of ${relations.length}.`,
      { created: created.length, proposals: proposals.length, failed: failed.length, total: relations.length, results },
      next.length ? next : undefined,
    ),
  };
}

async function handleDismiss(from: string, to: string, type: string, score?: number) {
  requireWriteAccess();

  const fromEntry = await kernelQuery<{ _id: string } | null>("chain.getEntry", { entryId: from });
  if (!fromEntry) {
    return notFoundResult(from);
  }
  const toEntry = await kernelQuery<{ _id: string } | null>("chain.getEntry", { entryId: to });
  if (!toEntry) {
    return notFoundResult(to);
  }

  await kernelMutation("chain.dismissSuggestion", {
    fromEntryId: fromEntry._id,
    suggestedEntryId: toEntry._id,
    recommendedType: type,
    score: score ?? 0,
  });

  return successResult(
    `Dismissed suggestion: ${from} → ${to} (${type}). Feedback recorded for scoring improvements.`,
    `Dismissed suggestion ${from} → ${to} (${type}). Feedback recorded.`,
    { from, to, type },
  );
}

async function handleDelete(from: string, to: string, type: string) {
  requireWriteAccess();

  await kernelMutation<GatewayRouteReturnByName<"chain.removeEntryRelation">>("chain.removeEntryRelation", {
    fromEntryId: from,
    toEntryId: to,
    type,
  });

  return successResult(
    `Removed relation: ${from} —[${type}]→ ${to}.`,
    `Relation ${from} → ${to} (${type}) removed.`,
    { from, to, type },
    [{ tool: "relations", description: "Check remaining connections", parameters: { action: "find", entryId: from } }],
  );
}
